import queryString from 'query-string'

import { fetchingAPI, apiService } from '@/api'

import { setLoading, setData, setError } from './omnichannelUsage.redux'

/**
 * Fetch omnichannel usage for an organization.
 *
 * No date range is sent. The window is the contract year, which only the server
 * can derive (it comes from Salesforce), so asking for it by omission keeps the
 * headline and the table on the same window instead of having the client guess
 * one and drift (QCER-1104).
 *
 * The endpoint always returns:
 *   - contract_limit                    (sourced from Salesforce, with fallbacks)
 *   - contract_start + contract_end     (bounds of the contract year shown)
 *   - headline:  totals over that window (drives the progress bar)
 *   - table:     the same totals, broken down by channel
 *
 * @param {object} params
 * @param {number} [params.organizationId] Org to report on; defaults server-side
 *   to the authenticated user's org.
 * @param {() => boolean} [params.isCurrent] Returns false once this request has
 *   been superseded. Switching organization twice in quick succession leaves two
 *   requests in flight, and without this the slower one wins on arrival — its
 *   `setData` would clear a newer request's error and repaint the previous org's
 *   counts, which is the stale-numbers bug this page exists to avoid. Mirrors
 *   the `requestIdRef` guard in KodiUsagePage.
 */
export const fetchOmnichannelUsage =
  ({ organizationId, isCurrent = () => true } = {}) =>
  async (dispatch) => {
    dispatch(setLoading(true))

    try {
      const params = {}
      if (organizationId) {
        params.organization_id = organizationId
      }

      const query = queryString.stringify(params)
      const response = await fetchingAPI(
        `${apiService.reporting}/api/usage/omnichannel${query ? `?${query}` : ''}`,
        'GET',
        dispatch
      )
      if (isCurrent()) {
        dispatch(setData(response))
      }
    } catch (error) {
      console.error('Error fetching omnichannel usage:', error)
      if (isCurrent()) {
        dispatch(setError({ kind: 'fetchFailed', message: error?.message }))
      }
    } finally {
      // A superseded request must not clear the loading flag either — the
      // request that replaced it is still running and owns that transition.
      if (isCurrent()) {
        dispatch(setLoading(false))
      }
    }
  }
